import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { Child } from '@aba-denver/core';
import { listUsers, setUserDisabled, ManagedUser } from '../../lib/functions';
import { useData } from '../../contexts/DataContext';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { patientAgeLabel, formatDate, esdmStatus } from '../../lib/format';

type PatientWithOwner = Child & { ownerId?: string };

export function TherapistDetail() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { children, getChildEsdmProgress } = useData();
  const [therapist, setTherapist] = useState<ManagedUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const users = await listUsers();
      setTherapist(users.find((u) => u.uid === uid) ?? null);
    } catch (err) {
      console.error(err);
      setError('Não foi possível carregar o terapeuta. Verifique se você tem acesso de administrador.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const patients = useMemo(() => {
    return (children as PatientWithOwner[])
      .filter((c) => c.ownerId === uid)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [children, uid]);

  const toggleDisabled = async () => {
    if (!therapist) return;
    setBusy(true);
    try {
      await setUserDisabled(therapist.uid, !therapist.disabled);
      await load();
    } catch (err) {
      console.error(err);
      alert('Ação não concluída. Tente novamente.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
        <Spinner />
      </div>
    );
  }

  if (error) {
    return <div className="card" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}>{error}</div>;
  }

  if (!therapist) {
    return (
      <div className="empty-state">
        Terapeuta não encontrado.{' '}
        <a onClick={() => navigate('/admin/therapists')} style={{ cursor: 'pointer' }}>
          Voltar
        </a>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <a onClick={() => navigate('/admin/therapists')} style={{ cursor: 'pointer', fontSize: 13 }}>
            ‹ Terapeutas
          </a>
          <h1 style={{ marginTop: 6 }}>{therapist.displayName ?? therapist.email ?? therapist.uid}</h1>
        </div>
        <div className="row">
          <button className="btn secondary" onClick={() => navigate(`/admin/therapists/${therapist.uid}/edit`)}>
            Editar
          </button>
          <button className={therapist.disabled ? 'btn secondary' : 'btn danger'} disabled={busy} onClick={toggleDisabled}>
            {busy ? '…' : therapist.disabled ? 'Ativar' : 'Desativar'}
          </button>
        </div>
      </div>

      <div className="card">
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
          <div>
            <div className="muted" style={{ fontSize: 13 }}>E-mail</div>
            <div style={{ fontWeight: 600 }}>{therapist.email ?? '—'}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 13 }}>Situação</div>
            <div>{therapist.disabled ? <Badge variant="danger">Desativado</Badge> : <Badge variant="success">Ativo</Badge>}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 13 }}>Cadastrado em</div>
            <div style={{ fontWeight: 600 }}>{formatDate(therapist.createdAt)}</div>
          </div>
          <div>
            <div className="muted" style={{ fontSize: 13 }}>Último acesso</div>
            <div style={{ fontWeight: 600 }}>{formatDate(therapist.lastSignInAt)}</div>
          </div>
        </div>
      </div>

      <div className="card">
        <h2 style={{ marginBottom: 16 }}>Pacientes ({patients.length})</h2>
        {patients.length === 0 ? (
          <div className="empty-state" style={{ padding: 24 }}>Nenhum paciente vinculado.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Idade</th>
                  <th>Checklist ESDM</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {patients.map((p) => {
                  const status = esdmStatus(getChildEsdmProgress(p.id));
                  return (
                    <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/patients/${p.id}`)}>
                      <td style={{ fontWeight: 600 }}>{p.name}</td>
                      <td>{patientAgeLabel(p)}</td>
                      <td>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="muted">›</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
